<template>
  <div class="pdf-merger">
    <div class="merge-header">
      <h2 class="tool-title">{{ $t('merge.title') }}</h2>
      <p class="tool-description">{{ $t('merge.description') }}</p>
    </div>

    <div class="card">
      <FileDropZone
        :text="$t('merge.selectFiles')"
        :subtext="$t('split.orDrag')"
        v-model="selectedFiles"
        :multiple="true"
      />

      <div v-if="selectedFiles.length > 0" class="merge-options">
        <div class="files-list">
          <div class="list-header">
            <h3 class="list-title">{{ $t('merge.selectFiles') }} ({{ selectedFiles.length }})</h3>
            <p class="list-help">{{ $t('merge.reorderHelp') }}</p>
          </div>

          <div class="file-items">
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="file-item"
              :draggable="true"
              @dragstart="handleDragStart(index)"
              @dragover.prevent
              @drop="handleDrop(index)"
            >
              <div class="file-item-drag">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 8h16M4 16h16"
                  />
                </svg>
              </div>

              <div class="file-item-icon">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>

              <div class="file-item-info">
                <p class="file-item-name">{{ file.name }}</p>
                <p class="file-item-meta">{{ formatFileSize(file.size) }}</p>
              </div>

              <div class="file-item-order">{{ index + 1 }}</div>

              <button @click="removeFile(index)" class="file-item-remove">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          <button @click="addMoreFiles" class="btn-secondary w-full">
            {{ $t('merge.addMore') }}
          </button>
        </div>

        <div class="action-section">
          <button
            @click="handleMerge"
            :disabled="loading || selectedFiles.length < 2"
            class="btn-primary w-full"
          >
            <span v-if="loading">{{ $t('merge.processing') }}</span>
            <span v-else>{{ $t('merge.mergeButton') }}</span>
          </button>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <div v-if="success" class="success-message">
          {{ success }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import FileDropZone from './FileDropZone.vue'
import { usePdfHandler } from '@/composables/usePdfHandler'

const selectedFiles = ref<File[]>([])
const draggedIndex = ref<number | null>(null)

const { loading, error, mergePdf, downloadPdf } = usePdfHandler()
const success = ref<string | null>(null)

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

function handleDragStart(index: number) {
  draggedIndex.value = index
}

function handleDrop(dropIndex: number) {
  if (draggedIndex.value === null || draggedIndex.value === dropIndex) return

  const file = selectedFiles.value[draggedIndex.value]
  selectedFiles.value.splice(draggedIndex.value, 1)
  selectedFiles.value.splice(dropIndex, 0, file)

  draggedIndex.value = null
}

function removeFile(index: number) {
  selectedFiles.value.splice(index, 1)
}

function addMoreFiles() {
  const input = document.createElement('input')
  input.type = 'file'
  input.multiple = true
  input.accept = 'application/pdf'
  input.onchange = (e) => {
    const target = e.target as HTMLInputElement
    if (target.files) {
      selectedFiles.value.push(...Array.from(target.files))
    }
  }
  input.click()
}

async function handleMerge() {
  if (selectedFiles.value.length < 2) return

  try {
    success.value = null
    const blob = await mergePdf(selectedFiles.value)

    const timestamp = new Date().toISOString().slice(0, 10)
    downloadPdf(blob, `merged_${timestamp}.pdf`)

    success.value = $t('merge.success')
  } catch (err) {
    console.error('Merge error:', err)
  }
}
</script>

<style scoped>
.pdf-merger {
  @apply max-w-2xl mx-auto;
}

.merge-header {
  @apply mb-6 text-center;
}

.tool-title {
  @apply text-3xl font-bold text-gray-900 mb-2;
}

.tool-description {
  @apply text-gray-600;
}

.merge-options {
  @apply mt-6 space-y-6;
}

.files-list {
  @apply space-y-4;
}

.list-header {
  @apply space-y-1;
}

.list-title {
  @apply text-lg font-semibold text-gray-900;
}

.list-help {
  @apply text-sm text-gray-500;
}

.file-items {
  @apply space-y-2;
}

.file-item {
  @apply flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 cursor-move;
}

.file-item:hover {
  @apply bg-gray-100;
}

.file-item-drag {
  @apply text-gray-400;
}

.file-item-icon {
  @apply w-10 h-10 text-red-500;
}

.file-item-info {
  @apply flex-1 min-w-0;
}

.file-item-name {
  @apply font-medium text-gray-900 truncate;
}

.file-item-meta {
  @apply text-sm text-gray-500;
}

.file-item-order {
  @apply w-8 h-8 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-full font-semibold;
}

.file-item-remove {
  @apply p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors;
}

.action-section {
  @apply pt-4 border-t border-gray-200;
}

.error-message {
  @apply p-4 bg-red-50 text-red-700 rounded-lg;
}

.success-message {
  @apply p-4 bg-green-50 text-green-700 rounded-lg;
}

.btn-primary:disabled {
  @apply opacity-50 cursor-not-allowed;
}
</style>
